import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Sun, Moon, Home } from 'lucide-react';
import { SiDiscord, SiInstagram } from '@icons-pack/react-simple-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useToolStationVisible } from '../contexts/ToolStationVisibleContext';
import clsx from 'clsx';
import { useState } from 'react';
import { GoogleAd } from './GoogleAd';

interface LayoutProps {
  children: ReactNode;
}

/**
 * 工具站通用 Layout：Desktop 左側導航 + 中央主區，Mobile 單欄。
 */
export function Layout({ children }: LayoutProps) {
  const { tools, loadError } = useToolStationVisible();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#f7f8fb] text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      {/* 工具顯示設定載入失敗時提示（多為 CORS 或未設定 VITE_API_BASE） */}
      {loadError && (
        <div
          className="bg-amber-50 px-4 py-2 text-center text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
          role="status"
        >
          無法載入工具顯示設定，目前顯示全部工具。請確認後端 VITE_API_BASE 與
          CORS 已設定。
        </div>
      )}
      {/* 頂欄：Logo、回首頁（非首頁時）、深色模式、手機漢堡選單 */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-200/70 bg-white/90 px-4 py-3 backdrop-blur dark:border-gray-700 dark:bg-gray-800/90 lg:px-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="lg:hidden rounded-full border border-gray-200 p-2 text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            onClick={() => setMobileNavOpen(o => !o)}
            aria-label="開啟選單"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Link
            to="/"
            className="text-lg font-semibold tracking-tight text-gray-900 dark:text-white"
          >
            工具站
          </Link>
          {!isHome && (
            <Link
              to="/"
              className="hidden items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition hover:border-gray-300 hover:bg-gray-100 hover:text-primary dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 lg:flex"
            >
              <Home className="h-4 w-4" />
              回首頁
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a
            href="https://discord.gg/EZeqWHmj"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-gray-200 p-2 text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            aria-label="Discord"
          >
            <SiDiscord className="h-5 w-5" />
          </a>
          <a
            href="https://www.instagram.com/emvyax"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-gray-200 p-2 text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            aria-label="Instagram"
          >
            <SiInstagram className="h-5 w-5" />
          </a>
          <button
            type="button"
            className="rounded-full border border-gray-200 p-2 text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? '切換淺色' : '切換深色'}
          >
            {theme === 'dark' ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </button>
        </div>
      </header>

      {/* 手機：漢堡展開的導航 */}
      {mobileNavOpen && (
        <div className="lg:hidden border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 px-4 py-3">
          <Link
            to="/"
            className="block rounded-xl px-3 py-2 text-gray-700 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            onClick={() => setMobileNavOpen(false)}
          >
            首頁
          </Link>
          {tools.map(t => (
            <Link
              key={t.id}
              to={`/tools/${t.slug}`}
              className="block rounded-xl px-3 py-2 text-gray-700 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              onClick={() => setMobileNavOpen(false)}
            >
              {t.name}
            </Link>
          ))}
          <div className="mt-3 flex gap-2 border-t border-gray-200 pt-3 dark:border-gray-600">
            <a
              href="https://discord.gg/EZeqWHmj"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-gray-200 p-2 text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              aria-label="Discord"
            >
              <SiDiscord className="h-5 w-5" />
            </a>
            <a
              href="https://www.instagram.com/emvyax"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-gray-200 p-2 text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              aria-label="Instagram"
            >
              <SiInstagram className="h-5 w-5" />
            </a>
          </div>
        </div>
      )}

      <div className="mx-auto flex max-w-[1400px]">
        {/* 左側欄：Desktop 顯示 */}
        <aside className="hidden w-[220px] shrink-0 flex-col border-r border-gray-200/70 bg-white/85 backdrop-blur dark:border-gray-700 dark:bg-gray-800/80 lg:flex">
          <nav className="p-4 space-y-1">
            <Link
              to="/"
              className={clsx(
                'block rounded-xl px-3 py-2 text-sm transition',
                isHome
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              )}
            >
              首頁
            </Link>
            {tools.map(t => (
              <Link
                key={t.id}
                to={`/tools/${t.slug}`}
                className={clsx(
                  'block rounded-xl px-3 py-2 text-sm transition',
                  location.pathname === `/tools/${t.slug}`
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                )}
              >
                {t.name}
              </Link>
            ))}
            <div className="mt-4 flex gap-2 border-t border-gray-200 pt-4 dark:border-gray-600">
              <a
                href="https://discord.gg/EZeqWHmj"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-gray-200 p-2 text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Discord"
              >
                <SiDiscord className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/emvyax"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-gray-200 p-2 text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Instagram"
              >
                <SiInstagram className="h-5 w-5" />
              </a>
            </div>
          </nav>

          {/* 左側廣告位 */}
          <div className="mt-auto p-4 border-t border-gray-200 dark:border-gray-600">
            <GoogleAd format="vertical" style={{ minHeight: '300px' }} />
          </div>
        </aside>

        {/* 中間主區與右側廣告區 */}
        <div className="flex flex-1 min-w-0">
          <main className="min-w-0 flex-1 px-4 py-6 lg:max-w-[920px] lg:px-8">
            {children}
          </main>

          {/* 右側廣告欄：大螢幕時顯示在右側空白處 */}
          <aside className="hidden w-[300px] shrink-0 flex-col p-6 xl:flex">
            <div className="sticky top-20">
              <GoogleAd format="rectangle" style={{ minHeight: '600px' }} />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
